import { useState, useEffect, useCallback } from "react";
import { Plus, Pencil, Trash2, CreditCard as CreditCardIcon, AlertCircle, ArrowLeft, RefreshCw, Receipt, Download } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import api, { formatCurrency, handleNumericInput, parseNumericValue, getBankAccounts, formatIndianNumber } from "../lib/api";
import { toast } from "sonner";
import { downloadFile } from "../lib/download";

const emptyCard = {
  card_name: "",
  bank_name: "",
  card_type: "credit",
  credit_limit: "",
  current_balance: "",
  billing_date: "",
  due_date: "",
  min_payment_percent: 5,
  notes: "",
};

const cardColors = [
  "from-slate-800 to-slate-900",
  "from-blue-600 to-blue-800",
  "from-emerald-600 to-emerald-800",
  "from-orange-500 to-orange-700",
  "from-red-600 to-red-800",
  "from-indigo-600 to-indigo-800",
];

const MONTH_OPTIONS = (() => {
  const options = [];
  const now = new Date();
  for (let i = -12; i <= 6; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
    const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const label = d.toLocaleString('en-IN', { month: 'short', year: 'numeric' });
    options.push({ value: val, label });
  }
  return options;
})();

const StatusBadge = ({ status }) => {
  const cls = {
    pending: "bg-amber-100 text-amber-700",
    partial: "bg-blue-100 text-blue-700",
    paid: "bg-emerald-100 text-emerald-700",
  };
  return (
    <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${cls[status] || cls.pending}`}>
      {status === "pending" ? "Pending" : status === "partial" ? "Partial" : "Paid"}
    </span>
  );
};

export default function CreditCards() {
  const { canEdit } = useAuth();
  const [cards, setCards] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const [formData, setFormData] = useState(emptyCard);

  // Detail view state
  const [detailCard, setDetailCard] = useState(null);
  const [bills, setBills] = useState([]);
  const [billMonth, setBillMonth] = useState(
    `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`
  );
  const [billAmount, setBillAmount] = useState("");
  const [paymentAmounts, setPaymentAmounts] = useState({});
  const [selectedBanks, setSelectedBanks] = useState({});
  const [paymentDates, setPaymentDates] = useState({});
  const [savingId, setSavingId] = useState(null);

  // Export dialog state
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportFormat, setExportFormat] = useState("pdf");
  const [exportCardId, setExportCardId] = useState("all");
  const [exportStartMonth, setExportStartMonth] = useState(
    `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`
  );
  const [exportEndMonth, setExportEndMonth] = useState(
    `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}`
  );

  const [exporting, setExporting] = useState(false);

  const fetchCards = useCallback(async () => {
    try {
      const [cardsRes, banksRes] = await Promise.all([
        api.get("/credit-cards"),
        getBankAccounts(),
      ]);
      setCards(cardsRes.data);
      setBankAccounts(banksRes.data);
    } catch (error) {
      toast.error("Failed to load credit cards");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchBills = async (cardId) => {
    try {
      const res = await api.get(`/credit-cards/${cardId}/bills`);
      setBills(res.data);
    } catch (error) {
      toast.error("Failed to load bills");
    }
  };

  useEffect(() => { fetchCards(); }, [fetchCards]);

  // Open detail view for a card
  const openDetailView = (card) => {
    setDetailCard(card);
    fetchBills(card.id);
  };

  const closeDetailView = () => {
    setDetailCard(null);
    setBills([]);
    setBillAmount("");
    setPaymentAmounts({});
    setSelectedBanks({});
    fetchCards(); // Refresh card data
  };

  const handleOpenDialog = (card = null) => {
    if (card) {
      setSelectedCard(card);
      setFormData({
        card_name: card.card_name,
        bank_name: card.bank_name,
        card_type: card.card_type,
        credit_limit: card.credit_limit,
        current_balance: card.current_balance,
        billing_date: card.billing_date || "",
        due_date: card.due_date || "",
        min_payment_percent: card.min_payment_percent,
        notes: card.notes || "",
      });
    } else {
      setSelectedCard(null);
      setFormData(emptyCard);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedCard(null);
    setFormData(emptyCard);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        credit_limit: parseNumericValue(formData.credit_limit),
        current_balance: parseNumericValue(formData.current_balance),
      };
      if (selectedCard) {
        await api.put(`/credit-cards/${selectedCard.id}`, submitData);
        toast.success("Credit card updated");
      } else {
        await api.post("/credit-cards", submitData);
        toast.success("Credit card added");
      }
      handleCloseDialog();
      fetchCards();
    } catch (error) {
      toast.error("Failed to save credit card");
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/credit-cards/${selectedCard.id}`);
      toast.success("Credit card deleted");
      setDeleteDialogOpen(false);
      setSelectedCard(null);
      if (detailCard?.id === selectedCard.id) closeDetailView();
      fetchCards();
    } catch (error) {
      toast.error("Failed to delete credit card");
    }
  };

  // Bill operations
  const handleAddBill = async () => {
    const amount = parseNumericValue(billAmount);
    if (!amount || amount <= 0) {
      toast.error("Enter a valid bill amount");
      return;
    }
    try {
      await api.post(`/credit-cards/${detailCard.id}/bills`, {
        month: billMonth,
        bill_amount: amount,
      });
      toast.success(`Bill for ${billMonth} added/updated`);
      setBillAmount("");
      fetchBills(detailCard.id);
    } catch (error) {
      toast.error("Failed to add bill");
    }
  };

  const handlePayBill = async (bill) => {
    const amountStr = paymentAmounts[bill.id];
    const amount = parseNumericValue(amountStr);
    const bankId = selectedBanks[bill.id];
    const paidDate = paymentDates[bill.id] || new Date().toISOString().slice(0, 10);
    if (!amount || amount <= 0) { toast.error("Enter a valid payment amount"); return; }
    if (!bankId) { toast.error("Select a bank account"); return; }
    try {
      setSavingId(bill.id);
      await api.post(`/credit-cards/${detailCard.id}/bills/${bill.id}/pay`, {
        amount, bank_account_id: bankId, paid_date: paidDate,
      });
      toast.success(`Payment of ${formatCurrency(amount)} recorded for ${paidDate}`);
      setPaymentAmounts(p => { const s = { ...p }; delete s[bill.id]; return s; });
      setSelectedBanks(p => { const s = { ...p }; delete s[bill.id]; return s; });
      setPaymentDates(p => { const s = { ...p }; delete s[bill.id]; return s; });
      fetchBills(detailCard.id);
      // Refresh bank accounts and card data
      const [cardsRes, banksRes] = await Promise.all([
        api.get("/credit-cards"),
        getBankAccounts(),
      ]);
      setCards(cardsRes.data);
      setBankAccounts(banksRes.data);
      setDetailCard(cardsRes.data.find(c => c.id === detailCard.id) || detailCard);
    } catch (error) {
      toast.error("Failed to record payment");
    } finally {
      setSavingId(null);
    }
  };

  const handleResetBill = async (bill) => {
    try {
      setSavingId(bill.id);
      await api.post(`/credit-cards/${detailCard.id}/bills/${bill.id}/reset`);
      toast.success("Payment reset (bank balance restored)");
      fetchBills(detailCard.id);
      const [cardsRes, banksRes] = await Promise.all([
        api.get("/credit-cards"),
        getBankAccounts(),
      ]);
      setCards(cardsRes.data);
      setBankAccounts(banksRes.data);
      setDetailCard(cardsRes.data.find(c => c.id === detailCard.id) || detailCard);
    } catch (error) {
      toast.error("Failed to reset payment");
    } finally {
      setSavingId(null);
    }
  };

  const handleDeleteBill = async (bill) => {
    if (!window.confirm(`Delete bill for ${bill.month}? This will refund any payments to the bank.`)) return;
    try {
      setSavingId(bill.id);
      await api.delete(`/credit-cards/${detailCard.id}/bills/${bill.id}`);
      toast.success("Bill deleted");
      fetchBills(detailCard.id);
      const [cardsRes, banksRes] = await Promise.all([
        api.get("/credit-cards"),
        getBankAccounts(),
      ]);
      setCards(cardsRes.data);
      setBankAccounts(banksRes.data);
      setDetailCard(cardsRes.data.find(c => c.id === detailCard.id) || detailCard);
    } catch (error) {
      toast.error("Failed to delete bill");
    } finally {
      setSavingId(null);
    }
  };

  const totalCreditLimit = cards.reduce((sum, c) => sum + c.credit_limit, 0);

  const handleExport = async () => {
    try {
      setExporting(true);
      const params = new URLSearchParams({
        format: exportFormat,
        card_id: exportCardId,
        start_month: exportStartMonth,
        end_month: exportEndMonth,
      });
      const url = `/api/credit-cards/export?${params.toString()}`;
      const filename = `credit_cards_${new Date().toISOString().split('T')[0]}.${exportFormat}`;
      
      // Close dialog FIRST so its overlay doesn't block the download
      setExportDialogOpen(false);
      
      const success = await downloadFile(url, filename);
      if (success) {
        toast.success(`${exportFormat.toUpperCase()} downloaded successfully`);
      }
    } catch (error) {
      toast.error("Failed to export");
    } finally {
      setExporting(false);
    }
  };

  const totalBalance = cards.reduce((sum, c) => sum + c.current_balance, 0);
  const totalAvailable = totalCreditLimit - totalBalance;
  const utilizationPercent = totalCreditLimit > 0 ? (totalBalance / totalCreditLimit) * 100 : 0;

  // ============ DETAIL VIEW ============
  if (detailCard) {
    const totalBilled = bills.reduce((s, b) => s + b.bill_amount, 0);
    const totalPaid = bills.reduce((s, b) => s + (b.amount_paid || 0), 0);
    const totalPending = bills.reduce((s, b) => s + (b.pending || 0), 0);

    return (
      <div className="space-y-6 animate-fade-in" data-testid="credit-card-detail">
        {/* Header */}
        <div className="page-header">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={closeDetailView} data-testid="back-to-cards">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="page-title">{detailCard.card_name}</h1>
              <p className="text-sm text-slate-500 mt-1">{detailCard.bank_name} &bull; Limit: {formatCurrency(detailCard.credit_limit)}</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => handleOpenDialog(detailCard)} data-testid="edit-card-detail">
            <Pencil className="w-4 h-4 mr-2" /> Edit Card
          </Button>
        </div>

        {/* Card Summary */}
        <Card className="bg-brand-navy text-white">
          <CardContent className="p-4 sm:p-6">
            <div className="grid grid-cols-2 gap-3 sm:gap-6 md:grid-cols-4">
              <div>
                <p className="text-white/70 text-xs sm:text-sm">Current Balance</p>
                <p className="text-lg sm:text-2xl font-bold tabular-nums">{formatCurrency(detailCard.current_balance)}</p>
              </div>
              <div>
                <p className="text-white/70 text-xs sm:text-sm">Total Billed</p>
                <p className="text-lg sm:text-2xl font-bold tabular-nums">{formatCurrency(totalBilled)}</p>
              </div>
              <div>
                <p className="text-white/70 text-xs sm:text-sm">Total Paid</p>
                <p className="text-lg sm:text-2xl font-bold tabular-nums text-emerald-300">{formatCurrency(totalPaid)}</p>
              </div>
              <div>
                <p className="text-white/70 text-xs sm:text-sm">Total Pending</p>
                <p className="text-lg sm:text-2xl font-bold tabular-nums text-amber-300">{formatCurrency(totalPending)}</p>
              </div>
            </div>
            {detailCard.billing_date && (
              <div className="flex gap-6 mt-4 pt-4 border-t border-white/10 text-sm">
                <p><span className="text-white/70">Bill Day:</span> <span className="font-medium">{detailCard.billing_date}{detailCard.billing_date === '1' ? 'st' : detailCard.billing_date === '2' ? 'nd' : detailCard.billing_date === '3' ? 'rd' : 'th'} of every month</span></p>
                <p><span className="text-white/70">Due Day:</span> <span className="font-medium">{detailCard.due_date}{detailCard.due_date === '1' ? 'st' : detailCard.due_date === '2' ? 'nd' : detailCard.due_date === '3' ? 'rd' : 'th'} of every month</span></p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Add Bill */}
        <Card>
          <CardContent className="p-4">
            <h3 className="font-semibold text-sm text-slate-700 mb-3">Add / Update Monthly Bill</h3>
            <div className="flex flex-col sm:flex-row items-end gap-3">
              <div className="w-full sm:w-40">
                <Label className="text-xs">Month</Label>
                <Select value={billMonth} onValueChange={setBillMonth}>
                  <SelectTrigger data-testid="bill-month-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {MONTH_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-full sm:w-48">
                <Label className="text-xs">Bill Amount (&#8377;)</Label>
                <Input
                  type="text" inputMode="decimal" value={billAmount}
                  onChange={(e) => setBillAmount(handleNumericInput(e.target.value))}
                  placeholder="Enter bill amount"
                  data-testid="bill-amount-input"
                />
              </div>
              <Button
                onClick={handleAddBill}
                className="bg-brand-orange hover:bg-[#e09515] text-brand-navy h-10"
                data-testid="add-bill-btn"
              >
                <Plus className="w-4 h-4 mr-2" /> Add Bill
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Bills Table */}
        {bills.length > 0 ? (
          <Card>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Month</TableHead>
                    <TableHead className="text-right">Bill Amount</TableHead>
                    <TableHead className="text-right w-44">Pay Amount</TableHead>
                    <TableHead className="text-right">Paid So Far</TableHead>
                    <TableHead className="text-right">Pending</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-center w-36">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {bills.map((bill) => {
                    const isFullyPaid = (bill.pending || 0) <= 0;
                    const paidSoFar = bill.amount_paid || 0;
                    const payAmt = paymentAmounts[bill.id] || "";
                    return (
                      <TableRow
                        key={bill.id}
                        data-testid={`bill-row-${bill.id}`}
                        className={isFullyPaid ? "bg-emerald-50/50" : bill.status === "partial" ? "bg-blue-50/50" : ""}
                      >
                        <TableCell className="font-medium">
                          {MONTH_OPTIONS.find(o => o.value === bill.month)?.label || bill.month}
                        </TableCell>
                        <TableCell className="text-right font-semibold tabular-nums text-brand-navy">
                          {formatCurrency(bill.bill_amount)}
                        </TableCell>
                        <TableCell className="text-right">
                          {!isFullyPaid ? (
                            <div className="flex flex-col gap-1">
                              <div className="relative inline-flex">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">&#8377;</span>
                                <Input
                                  type="text" inputMode="decimal" value={payAmt}
                                  onChange={(e) => setPaymentAmounts(p => ({ ...p, [bill.id]: handleNumericInput(e.target.value) }))}
                                  onBlur={(e) => {
                                    const val = parseNumericValue(e.target.value);
                                    if (val > 0) setPaymentAmounts(p => ({...p, [bill.id]: formatIndianNumber(val)}));
                                  }}
                                  className="text-right pr-3 pl-7 w-36 font-medium"
                                  placeholder={formatIndianNumber(bill.pending || 0)}
                                  data-testid={`pay-input-${bill.id}`}
                                />
                              </div>
                              <Select
                                value={selectedBanks[bill.id] || ""}
                                onValueChange={(v) => setSelectedBanks(p => ({ ...p, [bill.id]: v }))}
                              >
                                <SelectTrigger className="w-36 h-8 text-xs" data-testid={`bank-select-${bill.id}`}>
                                  <SelectValue placeholder="From Bank..." />
                                </SelectTrigger>
                                <SelectContent>
                                  {bankAccounts.map((b) => (
                                    <SelectItem key={b.id} value={b.id} className="text-xs">
                                      {b.name} ({formatCurrency(b.balance)})
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <Input
                                type="date"
                                value={paymentDates[bill.id] || new Date().toISOString().slice(0, 10)}
                                onChange={(e) => setPaymentDates(p => ({ ...p, [bill.id]: e.target.value }))}
                                className="w-36 h-8 text-xs"
                                data-testid={`date-input-${bill.id}`}
                              />
                            </div>
                          ) : (
                            <span className="text-xs text-slate-500">{bill.paid_date || "-"}</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          <span className={paidSoFar > 0 ? "text-emerald-600 font-semibold" : "text-slate-400"}>
                            {formatCurrency(paidSoFar)}
                          </span>
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          <span className={(bill.pending || 0) > 0 ? "text-amber-600 font-semibold" : "text-emerald-600 font-semibold"}>
                            {formatCurrency(bill.pending || 0)}
                          </span>
                        </TableCell>
                        <TableCell><StatusBadge status={bill.status || "pending"} /></TableCell>
                        <TableCell className="text-center">
                          <div className="flex flex-col gap-1 items-center">
                            {!isFullyPaid && (
                              <Button
                                onClick={() => handlePayBill(bill)}
                                disabled={savingId === bill.id || !payAmt}
                                size="sm"
                                className="bg-emerald-600 hover:bg-emerald-700 text-white h-9 px-3 w-full"
                                data-testid={`pay-bill-btn-${bill.id}`}
                              >
                                {savingId === bill.id ? <RefreshCw className="w-4 h-4 animate-spin" /> : "Pay"}
                              </Button>
                            )}
                            {paidSoFar > 0 && (
                              <Button
                                onClick={() => handleResetBill(bill)}
                                disabled={savingId === bill.id}
                                size="sm" variant="outline"
                                className="h-8 px-3 text-xs w-full"
                                data-testid={`reset-bill-btn-${bill.id}`}
                              >
                                {savingId === bill.id ? <RefreshCw className="w-3 h-3 animate-spin" /> : "Undo"}
                              </Button>
                            )}
                            <div className="flex gap-1 w-full">
                              <Button
                                onClick={() => handleDeleteBill(bill)}
                                disabled={savingId === bill.id}
                                size="sm" variant="ghost"
                                className="h-7 px-2 text-red-500 hover:text-red-700 flex-1"
                                data-testid={`delete-bill-btn-${bill.id}`}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </Card>
        ) : (
          <Card>
            <CardContent className="py-12">
              <div className="empty-state">
                <Receipt className="empty-state-icon" />
                <h3 className="empty-state-title">No bills yet</h3>
                <p className="empty-state-text">Add a monthly bill above to start tracking payments</p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  // ============ CARDS GRID VIEW ============
  return (
    <div className="space-y-6 animate-fade-in" data-testid="credit-cards-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Credit Cards</h1>
          <p className="text-sm text-slate-500 mt-1">Click on a card to manage bills and payments</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setExportDialogOpen(true)} data-testid="export-credit-cards-btn">
            <Download className="w-4 h-4 mr-2" /> Export
          </Button>
          {canEdit && (
            <Button onClick={() => handleOpenDialog()} className="bg-brand-orange hover:bg-[#e09515] text-brand-navy" data-testid="add-credit-card-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Card
            </Button>
          )}
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-4">
        <Card><CardContent className="p-3 sm:p-4">
          <p className="text-xs sm:text-sm text-slate-500">Total Credit Limit</p>
          <p className="text-lg sm:text-2xl font-bold text-slate-900 tabular-nums mt-1">{formatCurrency(totalCreditLimit)}</p>
        </CardContent></Card>
        <Card><CardContent className="p-3 sm:p-4">
          <p className="text-xs sm:text-sm text-slate-500">Current Balance</p>
          <p className="text-lg sm:text-2xl font-bold text-red-600 tabular-nums mt-1">{formatCurrency(totalBalance)}</p>
        </CardContent></Card>
        <Card><CardContent className="p-3 sm:p-4">
          <p className="text-xs sm:text-sm text-slate-500">Available Credit</p>
          <p className="text-lg sm:text-2xl font-bold text-emerald-600 tabular-nums mt-1">{formatCurrency(totalAvailable)}</p>
        </CardContent></Card>
        <Card><CardContent className="p-3 sm:p-4">
          <p className="text-xs sm:text-sm text-slate-500">Utilization</p>
          <p className={`text-lg sm:text-2xl font-bold tabular-nums mt-1 ${utilizationPercent > 70 ? "text-red-600" : utilizationPercent > 30 ? "text-amber-600" : "text-emerald-600"}`}>
            {utilizationPercent.toFixed(1)}%
          </p>
        </CardContent></Card>
      </div>

      {/* Cards Grid */}
      {cards.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {cards.map((card, index) => {
            const utilization = card.credit_limit > 0 ? (card.current_balance / card.credit_limit) * 100 : 0;
            const color = cardColors[index % cardColors.length];
            return (
              <div key={card.id} className="relative group" data-testid={`credit-card-${card.id}`}>
                <div
                  className={`bg-gradient-to-br ${color} rounded-2xl p-6 text-white shadow-lg min-h-[200px] flex flex-col justify-between cursor-pointer transition-transform hover:scale-[1.02]`}
                  onClick={() => openDetailView(card)}
                  data-testid={`card-tile-${card.id}`}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-white/70 text-sm">{card.bank_name}</p>
                      <p className="font-bold text-lg mt-1">{card.card_name}</p>
                    </div>
                    <CreditCardIcon className="w-8 h-8 text-white/50" />
                  </div>
                  <div className="mt-4">
                    <p className="text-white/70 text-xs">Current Balance</p>
                    <p className="text-2xl font-bold tabular-nums">{formatCurrency(card.current_balance)}</p>
                  </div>
                  <div className="flex justify-between items-end mt-4">
                    <div>
                      <p className="text-white/70 text-xs">Credit Limit</p>
                      <p className="font-semibold tabular-nums">{formatCurrency(card.credit_limit)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-white/70 text-xs">Due Day</p>
                      <p className="font-semibold">{card.due_date ? `${card.due_date}th` : "Not set"}</p>
                    </div>
                  </div>
                  <div className="mt-4">
                    <div className="h-1.5 bg-white/20 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full ${utilization > 70 ? 'bg-red-400' : utilization > 30 ? 'bg-amber-400' : 'bg-emerald-400'}`}
                        style={{ width: `${Math.min(utilization, 100)}%` }}
                      />
                    </div>
                    <p className="text-white/70 text-xs mt-1 text-right">{utilization.toFixed(1)}% used</p>
                  </div>
                </div>
                {/* Action Buttons (top right) */}
                <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <Button variant="ghost" size="icon" className="h-8 w-8 bg-white/20 hover:bg-white/30 text-white"
                    onClick={(e) => { e.stopPropagation(); handleOpenDialog(card); }} data-testid={`edit-card-${card.id}`}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8 bg-white/20 hover:bg-red-500/50 text-white"
                    onClick={(e) => { e.stopPropagation(); setSelectedCard(card); setDeleteDialogOpen(true); }} data-testid={`delete-card-${card.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
                {utilization > 70 && (
                  <div className="absolute -top-2 -right-2 bg-red-500 text-white text-xs px-2 py-1 rounded-full flex items-center gap-1">
                    <AlertCircle className="w-3 h-3" /> High
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <Card><CardContent className="py-12">
          <div className="empty-state">
            <CreditCardIcon className="empty-state-icon" />
            <h3 className="empty-state-title">No credit cards yet</h3>
            <p className="empty-state-text">Add your credit cards to track bills and payments</p>
            <Button onClick={() => handleOpenDialog()} className="mt-4 bg-brand-orange hover:bg-[#e09515] text-brand-navy">
              <Plus className="w-4 h-4 mr-2" /> Add Card
            </Button>
          </div>
        </CardContent></Card>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="credit-card-dialog">
          <DialogHeader>
            <DialogTitle>{selectedCard ? "Edit Credit Card" : "Add Credit Card"}</DialogTitle>
            <DialogDescription>{selectedCard ? "Update your credit card details." : "Fill in the details to add a credit card."}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="card_name">Card Name</Label>
                <Input id="card_name" value={formData.card_name} onChange={(e) => setFormData({ ...formData, card_name: e.target.value })}
                  placeholder="e.g., HDFC Regalia" required data-testid="input-card-name" />
              </div>
              <div>
                <Label htmlFor="bank_name">Bank Name</Label>
                <Input id="bank_name" value={formData.bank_name} onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                  placeholder="e.g., HDFC Bank" required data-testid="input-bank-name" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="credit_limit">Credit Limit (&#8377;)</Label>
                <Input id="credit_limit" type="text" inputMode="decimal" value={formData.credit_limit}
                  onChange={(e) => setFormData({ ...formData, credit_limit: handleNumericInput(e.target.value) })}
                  placeholder="Enter credit limit" required data-testid="input-credit-limit" />
              </div>
              <div>
                <Label htmlFor="current_balance">Current Balance (&#8377;)</Label>
                <Input id="current_balance" type="text" inputMode="decimal" value={formData.current_balance}
                  onChange={(e) => setFormData({ ...formData, current_balance: handleNumericInput(e.target.value) })}
                  placeholder="Enter balance" required data-testid="input-current-balance" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="billing_date">Bill Generation Day</Label>
                <Select value={formData.billing_date} onValueChange={(val) => setFormData({ ...formData, billing_date: val })}>
                  <SelectTrigger data-testid="input-billing-date"><SelectValue placeholder="Select day" /></SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
                      <SelectItem key={d} value={String(d)}>{d}{d === 1 ? 'st' : d === 2 ? 'nd' : d === 3 ? 'rd' : 'th'} of every month</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="due_date">Payment Due Day</Label>
                <Select value={formData.due_date} onValueChange={(val) => setFormData({ ...formData, due_date: val })}>
                  <SelectTrigger data-testid="input-due-date"><SelectValue placeholder="Select day" /></SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 31 }, (_, i) => i + 1).map(d => (
                      <SelectItem key={d} value={String(d)}>{d}{d === 1 ? 'st' : d === 2 ? 'nd' : d === 3 ? 'rd' : 'th'} of every month</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label htmlFor="min_payment_percent">Minimum Payment (%)</Label>
              <Input id="min_payment_percent" type="number" min="1" max="100" value={formData.min_payment_percent}
                onChange={(e) => setFormData({ ...formData, min_payment_percent: parseInt(e.target.value) || 5 })}
                data-testid="input-min-payment" />
            </div>
            <div>
              <Label htmlFor="notes">Notes (Optional)</Label>
              <Textarea id="notes" value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Any additional notes..." rows={2} data-testid="input-card-notes" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>Cancel</Button>
              <Button type="submit" className="bg-brand-orange hover:bg-[#e09515] text-brand-navy" data-testid="save-credit-card">
                {selectedCard ? "Update" : "Add Card"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Credit Card</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedCard?.card_name}"? All bills will also be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600" data-testid="confirm-delete-card">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Export Dialog */}
      <Dialog open={exportDialogOpen} onOpenChange={setExportDialogOpen}>
        <DialogContent className="max-w-md" data-testid="export-cc-dialog">
          <DialogHeader>
            <DialogTitle>Export Credit Card Statement</DialogTitle>
            <DialogDescription>Choose format, card, and date range to export.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Format</Label>
              <Select value={exportFormat} onValueChange={setExportFormat}>
                <SelectTrigger data-testid="export-format-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pdf">PDF</SelectItem>
                  <SelectItem value="csv">CSV</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Card</Label>
              <Select value={exportCardId} onValueChange={setExportCardId}>
                <SelectTrigger data-testid="export-card-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Cards</SelectItem>
                  {cards.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.card_name} ({c.bank_name})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>From Month</Label>
                <Select value={exportStartMonth} onValueChange={setExportStartMonth}>
                  <SelectTrigger data-testid="export-start-month"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {MONTH_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>To Month</Label>
                <Select value={exportEndMonth} onValueChange={setExportEndMonth}>
                  <SelectTrigger data-testid="export-end-month"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {MONTH_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExportDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleExport}
              disabled={exporting}
              className="bg-brand-orange hover:bg-[#e09515] text-brand-navy"
              data-testid="export-cc-submit"
            >
              {exporting ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
              Export
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
